import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { first } from 'rxjs';

import { AVATAR_SIZE } from '@celum/common-components/avatar';
import { CelumSystembarComponent, SimpleContextMenuItem } from '@celum/internal-components/systembar';
import { explicitEffect } from '@celum/shared/util';

import { SystembarFormatsComponent } from './systembar-formats/systembar-formats.component';
import { SystembarService } from './systembar.service';
import { NotificationsCenterComponent } from '../notifications/notifications-center/notifications-center.component';
import { UserAvatarComponent } from '../people/user-avatar/user-avatar.component';

export interface SystembarConfiguration {
  /**
   * Function to provide additional {@link SimpleContextMenuItem}s for the systembar
   */
  itemProvider?: () => SimpleContextMenuItem[];

  /**
   * Function to provide additional {@link SimpleContextMenuItem}s for the systembar's help context menu. The default items are passed as parameter
   * and can be manipulated (e.g. replaced, extended, etc.) by the function.
   *
   * @param defaultItems - default items of the systembar's help context menu
   */
  helpItemProvider?: (defaultItems: SimpleContextMenuItem[]) => SimpleContextMenuItem[];

  /**
   * Function which is called when the tenant is switched
   */
  onTenantSwitch?: () => void;
}

@Component({
  imports: [AsyncPipe, CelumSystembarComponent, NotificationsCenterComponent, SystembarFormatsComponent, UserAvatarComponent],
  selector: 'shared-systembar',
  templateUrl: './systembar.component.html',
  styleUrl: './systembar.component.scss',
  providers: [
    SystembarService,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' }
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SystembarComponent {
  public readonly configuration = input<SystembarConfiguration>();
  public readonly loadingReady = output<boolean>();

  protected readonly service = inject(SystembarService);

  protected AVATAR_SIZE = AVATAR_SIZE;

  constructor() {
    this.service.loadingReady.pipe(first()).subscribe(() => {
      this.loadingReady.emit(true);
    });

    explicitEffect(this.configuration, configuration => {
      if (configuration) {
        this.service.init(configuration);
      }
    });
  }
}
